/** @format */

import { FC, useCallback, useEffect, useRef, useState } from "react";
import {
  ClassicEditor as Editor,
  EventInfo,
  ClassicEditor,
  EditorConfig,
} from "ckeditor5";
import { Comments, TrackChangesData } from "ckeditor5-premium-features";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import "ckeditor5/ckeditor5.css";
import "@edres/ckeditor5-glossary/index.css";
/** This CSS was causing "start value has mixed support" warning so duplicated and fixed CSS in 2 places. start to flex-start. */
// import 'ckeditor5-premium-features/ckeditor5-premium-features.css';
import "./ckeditor5-premium-features.scss";
import "./VisualEditor.scss";
import { useGetUserQuery } from "../../app/services/userApi";
import { useAppSelector } from "../../app/hooks";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { CKEditorConfigs } from "./PluginConfigs";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { ckEditorApi } from "./ckEditorApi";
import { store } from "../../app/store";
import ErrorPage from "../errorPage/ErrorPage";

export type VisualEditorProps = {
  data?: string;
  editorId?: string;
  onReady?: (editor: Editor) => void;
  onChange?: (event: EventInfo<string, unknown>, editor: Editor) => void;
  onBlur?: (event: EventInfo<string, unknown>, editor: Editor) => void;
  onFocus?: (event: EventInfo<string, unknown>, editor: Editor) => void;
  forcePlainEditor?: boolean;
};

const VisualEditor: FC<VisualEditorProps> = ({
  data,
  editorId,
  onReady,
  onChange,
  onBlur,
  onFocus,
  forcePlainEditor = false,
}) => {
  const { t: _ } = useTranslation();
  const { userId } = useParams();
  const editorPresenceRef = useRef(null);
  const editorRef = useRef(null);
  const editorAnnotationsRef = useRef<HTMLDivElement | null>(null);
  const editorInstanceRef = useRef<ClassicEditor | null>(null);

  const token = useAppSelector((state) => state.auth.userToken);
  const {
    data: user,
    isSuccess: userLoaded,
    isFetching,
  } = useGetUserQuery(
    !!token?.token && !forcePlainEditor ? undefined : skipToken,
  );

  const [config, setConfig] = useState<{
    type: "premium" | "basic";
    config: EditorConfig & { unsplash: { apiUrl?: string; token?: string } };
  }>();
  const [isLayoutReady, setIsLayoutReady] = useState<boolean>(false);
  const [isEditorReady, setIsEditorReady] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>();

  // Change Annotation UI's (Anotation === comments) depending on window size
  const refreshDisplayMode = useCallback((editor?: Editor) => {
    if (
      (!editorInstanceRef?.current && !editor) ||
      !editorAnnotationsRef?.current
    ) {
      return;
    }

    const editorInstance = editor ?? editorInstanceRef.current;
    if (!editorInstance || !editorInstance.plugins.has("AnnotationsUIs"))
      return;

    const annotationsUIs = editorInstance.plugins.get("AnnotationsUIs");
    let commentsCount: number | undefined;

    if (editorInstance.plugins.has("CommentsRepository")) {
      const comments = editorInstance.plugins.get("CommentsRepository");
      // Fetch all comments that aren't:
      // archived / resolved / un-attached
      commentsCount =
        comments
          .getCommentThreads()
          .filter(
            (comment) =>
              !comment.isResolved &&
              comment.isAttached &&
              comment.archivedAt === null &&
              comment.comments.length > 0,
          )?.length ?? 0;
    }

    const sidebarElement = editorAnnotationsRef.current.parentElement;

    if (!sidebarElement) return;

    if (
      window.innerWidth < 1070 ||
      (commentsCount !== undefined && commentsCount <= 0)
    ) {
      sidebarElement.classList.remove("narrow");
      sidebarElement.classList.add("hidden");
      annotationsUIs.switchTo("inline");
    } else if (window.innerWidth < 1300) {
      // Narrow-sidebar: comments in sidebar and others inline
      sidebarElement.classList.remove("hidden");
      sidebarElement.classList.add("narrow");

      annotationsUIs.deactivateAll();
      annotationsUIs.activate(
        "narrowSidebar",
        // @ts-ignore
        (annotation) => annotation.type === "comment",
      );
      annotationsUIs.activate(
        "inline",
        // @ts-ignore
        (annotation) => annotation.type !== "comment",
      );
    } else {
      // Wide-sidebar: comments in sidebar and others inline
      sidebarElement.classList.remove("hidden", "narrow");
      annotationsUIs.deactivateAll();
      annotationsUIs.activate(
        "wideSidebar",
        // @ts-ignore
        (annotation) => annotation.type === "comment",
      );
      annotationsUIs.activate(
        "inline",
        // @ts-ignore
        (annotation) => annotation.type !== "comment",
      );
    }
  }, []);

  // Prevent CKEditor from firing requests after unloaded
  const checkPendingActions = useCallback((domEvt: Event) => {
    if (!editorInstanceRef?.current) {
      return;
    }

    const editorInstance = editorInstanceRef.current;

    if (
      editorInstance.plugins.has("PendingActions") &&
      editorInstance.plugins.get("PendingActions").hasAny
    ) {
      domEvt.preventDefault();
    }
  }, []);

  // Component has mounted check
  useEffect(() => {
    setIsLayoutReady(true);

    return () => {
      setIsLayoutReady(false);
      setIsEditorReady(false);
    };
  }, [isLayoutReady]);

  // Function to watch for comment events being fired (e.g. Adding comments)
  const setupListeners = useCallback(
    (editor: Editor) => {
      if (editor && editorId) {
        const editorInstance = editor;
        if (editorInstance.plugins.has("CommentsRepository")) {
          const comments = editorInstance.plugins.get("CommentsRepository");

          comments.on(
            `resolveCommentThread:${editorId}`,
            () => refreshDisplayMode,
          );
          comments.on(
            `submitCommentThread:${editorId}`,
            () => refreshDisplayMode,
          );

          // TODO: Use listener below to create mention notifications
          /* comments.on(`addComment:${editorId}`, ( evt, data ) => {
          console.log( evt, data );
        }); */

          comments.on(
            `removeCommentThread:${editorId}`,
            () => refreshDisplayMode,
          );
          comments.on(
            `updateCommentTbread:${editorId}`,
            () => refreshDisplayMode,
          );
        }
      }
    },
    [editorId, refreshDisplayMode],
  );

  // Function to setup track changes plugin on CKEditor
  const setupTrackChanges = useCallback((editor: Editor) => {
    if (editor) {
      const editorInstance = editor;

      if (editorInstance.plugins.has("TrackChangesEditing")) {
        const trackChangesEditing = editorInstance.plugins.get(
          "TrackChangesEditing",
        );

        if (trackChangesEditing.trackChangesCommand.value !== true) {
          // Enable glossary terms components
          trackChangesEditing.enableDefaultAttributesIntegration("glossary");
          trackChangesEditing.registerInlineAttribute("glossaryDefinition");
          trackChangesEditing.enableCommand("removeglossary");

          // Enable MathType and ChemType
          trackChangesEditing.enableCommand("MathType");
          trackChangesEditing.enableCommand("ChemType");
        }

        // Set track changes off by default
        trackChangesEditing.trackChangesCommand.value = false;
      }
    }
  }, []);

  useEffect(() => {
    if (isFetching) return;

    if (token !== undefined && new Date(token.expires) < new Date()) {
      setErrorMsg(_("ckEditor.tokenError"));
      return;
    }

    const unsplashConfig = {
      apiUrl: process.env.REACT_APP_RESOURCES_API_URL,
      token: token?.token,
    };

    setConfig(
      user?.role === "wjec" && userId === undefined
        ? {
            type: "premium",
            config: {
              ...CKEditorConfigs.staffConfig,
              unsplash: unsplashConfig,
              sidebar: {
                container: editorAnnotationsRef.current ?? undefined,
              },
              cloudServices: {
                uploadUrl: "https://100761.cke-cs.com/easyimage/upload/",
                tokenUrl: async (): Promise<string> => {
                  const token = await store.dispatch(
                    ckEditorApi.endpoints.getCloudJWT.initiate(),
                  );
                  if (token.error !== undefined || token.data === undefined) {
                    return Promise.reject("Failed to fetch token");
                  }
                  return token.data;
                },
                webSocketUrl: "wss://100761.cke-cs.com/ws",
              },
              collaboration: {
                channelId: editorId,
              },
              presenceList: {
                container: editorPresenceRef.current ?? undefined,
              },
              trackChangesData: {
                editorCreator: (
                  config: EditorConfig,
                  createElement: Function,
                ) => {
                  return Editor.create(createElement(), config);
                },
              },
            },
          }
        : {
            type: "basic",
            config: {
              ...CKEditorConfigs.publicConfig,
              unsplash: unsplashConfig,
            },
          },
    );

    return () => {
      setConfig(undefined);
    };
  }, [isFetching, user?.role, userId, editorId, userLoaded, token, _]);

  const onEditorReady = useCallback(
    (editor: Editor) => {
      editorInstanceRef.current = editor;
      setIsEditorReady(true);

      refreshDisplayMode(editor);

      window.addEventListener("resize", () => refreshDisplayMode(editor));
      window.addEventListener("beforeunload", checkPendingActions);

      setupListeners(editor);
      setupTrackChanges(editor);
    },
    [
      setupListeners,
      setupTrackChanges,
      refreshDisplayMode,
      checkPendingActions,
    ],
  );

  return (
    <>
      {errorMsg !== undefined ? (
        <ErrorPage
          actions={["reload", "report"]}
          statusMessage={errorMsg}
          className={"position-relative"}
        />
      ) : (
        <div className="main-container">
          <div className="presence mb-2" ref={editorPresenceRef}></div>
          <div className="editor-container editor-container_classic-editor editor-container_include-annotations">
            <div className="editor-container__editor-wrapper">
              <div className="editor-container__editor">
                {!!config && (
                  <div ref={editorRef}>
                    {isLayoutReady && (
                      <CKEditor
                        editor={Editor}
                        data={data}
                        config={config.config}
                        onReady={(editor) => {
                          onEditorReady(editor);
                          onReady && onReady(editor);
                        }}
                        onChange={(event, editor) => {
                          onChange && onChange(event, editor);
                        }}
                        onBlur={(event, editor) => {
                          onBlur && onBlur(event, editor);
                        }}
                        onFocus={(event, editor) => {
                          onFocus && onFocus(event, editor);
                        }}
                      />
                    )}
                  </div>
                )}
                {!isEditorReady && <p>{_("Loading")}...</p>}
              </div>
              <div className="editor-container__sidebar hidden">
                <div ref={editorAnnotationsRef}></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const getDataWithoutCollaboration = async (
  editor: Editor,
): Promise<string> => {
  let data: string;
  if (editor.plugins.has(TrackChangesData)) {
    try {
      const content = await (
        editor.plugins.get("TrackChangesData") as TrackChangesData
      ).getDataWithDiscardedSuggestions({ ignoreResolvedComments: true });
      data =
        typeof content === "string"
          ? content
          : Object.values(content).join(" ");
    } catch {
      console.error(
        "Failed to fetch data with discarded suggestions. Using getData instead.",
      );
      data = editor.getData({ ignoreResolvedComments: true });
    }
  } else {
    data = editor.getData({ ignoreResolvedComments: true });
  }

  if (editor.plugins.has(Comments) && data) {
    data = data.replace(
      /<comment-.*?.*?(?:\/)?>(.*?)<\/comment-.*?>|data-comment-.*?(["].*?["]|['].*?[']|[`].*?[`])/g,
      "",
    );
  }

  return data;
};

export default VisualEditor;
